import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import { useCustomerWorkspace } from "../../../context/CustomerWorkspaceContext";
import {
  fetchSearchPerformance,
  fetchMentionCheck,
  fetchMentionHistory,
  startMentionCheck,
  InsufficientBusinessDetail,
  CitedSource,
  LastAttempt,
  MentionCheck,
  MentionHistory,
  MentionHistoryPoint,
  MentionRole,
  SearchPerformance,
  VisibilityRow,
  VisibilitySummary,
  BingCrawlErrors,
  BingDataset,
  BingIndexHealth,
  BingSearchPerformance,
  SearchDisagreement,
  fetchWeeklySearch,
  WeeklySearch,
} from "../../../services/visibilityService";
import GbpProfileSection from "./GbpProfileSection";
import WeeklySearchCard from "./WeeklySearchCard";
import PromptSourceNote, { CONNECTIONS_PATH } from "./PromptSourceNote";
import ProfileEditOutcomes, { hasEditOutcomeList, hasEditOutcomesContent } from "./ProfileEditOutcomes";
import ProfileFindings, { hasProfileFindingsContent } from "./ProfileFindings";
import { engineLabel, roleTitle } from "./visibilityLabels";
import { BraveIndexCard, ClaudeCard, EngineGapsNote } from "./ClaudeBraveCards";

/**
 * AI Visibility dashboard (SGL-140).
 *
 * This is a near-literal port of the approved mockup
 * (https://claude.ai/code/artifact/5539ebe1-2c3c-49e2-aa3c-e68cfee48a32) —
 * same palette, type pairing (Fraunces/Hanken Grotesk), card radius, ring/
 * chart construction — scoped under `.avm-scope` with an `avm-` class prefix
 * so none of it leaks into the app's global Inter/theme-token styling.
 *
 * DATA STATUS (all real — no sample data on this page):
 *   - Score, KPIs, the question x engine matrix and competitors come from the
 *     AI mention check (SGL-135) via chat-service's /visibility/mentions
 *     proxy. Labeled "Live".
 *   - Search performance is real Google Search Console data, and Bing Webmaster
 *     Tools beside it (SGL-134), via /visibility/search-performance.
 *   - Trend is real run history (/visibility/mentions/history, SGL-88/139).
 *   - The actions feed has NO data source yet, so it renders an honest empty
 *     state rather than invented numbers. It and the other cards
 *     previously held mock content (competitor names, review-rating movement,
 *     published-post counts) which was removed once this page became something
 *     customers and prospects would actually see.
 */

/* ─────────────────────────── mock data (SGL-135/139 not live yet) ────── */

type Cell = "ok" | "mid" | "no" | "unknown";
// "?" not "✕" — an unmeasured cell must not borrow the vocabulary of a
// confirmed absence.
const CELL_LABEL: Record<Cell, string> = { ok: "✓", mid: "~", no: "✕", unknown: "?" };


/* ── real-data helpers (SGL-135) ───────────────────────────────────────── */

/**
 * Map a role onto the matrix's visual states.
 *
 * A cell we could not measure — a dead engine, a rate-limited classifier —
 * arrives as `null` or "unknown" and gets its own muted treatment. It must
 * never fall through to "no": that paints a red "you are invisible" square
 * for a question no engine ever answered, which is the failure that shipped
 * on 2026-08-25 when the OpenAI key ran out of credits mid-run.
 */
function roleToCell(role: MentionRole | null): Cell {
  if (role === "recommended" || role === "mentioned") return "ok";
  if (role === "cited_source_only") return "mid";
  if (role === "absent") return "no";
  return "unknown";
}

/** Headline above the score — never implies movement we cannot evidence. */
function scoreHeadline(m: MentionCheck | null, loading: boolean): string {
  if (loading) return "Checking…";
  if (!m) return "No check run yet";
  const s = m.score ?? 0;
  if (s === 0) return "Not showing up yet";
  if (s < 30) return "Barely visible";
  if (s < 60) return "Getting noticed";
  return "Well established";
}

/**
 * Competitors as bars, with the customer inserted at their real position.
 * Widths are relative to the most-named competitor rather than a percentage
 * of anything — "named 21 times" is a count, and presenting it as a rate
 * would invent a denominator we never measured.
 */
function competitorRows(m: MentionCheck, customerName?: string | null) {
  const comps = (m.competitors ?? []).slice(0, 8);
  const you = { name: customerName || "You", timesNamed: m.appearedIn ?? 0, you: true };
  const all = [...comps.map((c) => ({ name: c.name, timesNamed: c.times_named, you: false })), you];
  all.sort((a, b) => b.timesNamed - a.timesNamed);
  const max = Math.max(1, ...all.map((r) => r.timesNamed));
  return all.map((r) => ({ ...r, pct: Math.round((r.timesNamed / max) * 100) }));
}

/**
 * Why the latest check did not produce a result (SGL-139 AC5).
 *
 * Without this, a failed or skipped check is invisible: the dashboard keeps
 * showing the older result as if it were current. The reason codes come from
 * geo-visibility / chat-service; unknown codes fall through to a generic line
 * rather than showing an internal code to a customer.
 */
/** Where the owner edits the city and state the check is run for. */
const LOCATION_FIX_PATH = "/customer";

export function attemptMessage(a: LastAttempt): { title: string; body: string; href?: string; cta?: string } {
  const when = new Date(a.attemptedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const which = a.trigger === "scheduled" ? "automatic check" : "check";
  if (a.status === "failed") {
    return {
      title: `Your ${which} on ${when} couldn't finish`,
      // Failed runs raise the worker's Errors alarm, so "we've been alerted" is true here.
      body: "Something went wrong while checking the AI engines, so nothing was measured. We've been alerted, and the results below are from your previous check.",
    };
  }
  switch (a.reason) {
    case "insufficient_credits":
    case "no_credit_account":
      return {
        title: `Your ${which} on ${when} was skipped`,
        body: "Your credit balance is too low to run it. Add credits and the next automatic check will run.",
        href: "/billing", cta: "Add credits",
      };
    case "monthly_cap_reached":
      return {
        title: `Your ${which} on ${when} was skipped`,
        body: "This month's automatic checks have already run. The next one runs next month — or press Run check any time.",
      };
    case "location_unknown":
      // The city is the business's own (SGL-178), edited on the business
      // profile; the service areas on /brand-details no longer decide it.
      return {
        title: `Your ${which} on ${when} was skipped`,
        body: "We need to know where your business is based, so we can ask the questions your buyers ask in your city. Add your city and state to your business profile.",
        href: LOCATION_FIX_PATH, cta: "Open business profile",
      };
    case "insufficient_business_detail":
    case "brand_profile_unavailable":
      return {
        title: `Your ${which} on ${when} was skipped`,
        body: "We need to know what your business sells and where, so we can ask the questions your buyers ask.",
        href: "/brand-details", cta: "Open business settings",
      };
    case "plan_not_pro":
      // SGL-459: the sweep skips a Brand whose Owner isn't on a known Pro
      // plan — including one with no Owner or no plan on record, so this
      // says "couldn't confirm", not "you aren't on Pro".
      return {
        title: `Your ${which} on ${when} was skipped`,
        body: "Automatic checks are part of the Pro plan, and we couldn't confirm a Pro plan for this business.",
        href: "/billing", cta: "Check your plan",
      };
    default:
      return {
        title: `Your ${which} on ${when} didn't run`,
        // No alarm backs every skip reason, so no claim that anyone was told.
        body: "A problem on our side stopped it. Your previous results are shown below.",
      };
  }
}

/**
 * Whether two plotted points asked the same questions.
 *
 * Compared directly rather than read from `comparableToPrevious`, because that
 * flag is relative to the immediately preceding run — which may be one of the
 * points filtered out of the chart. Unknown (a run from before question sets
 * were recorded) counts as not comparable, matching the backend's delta rule.
 */
function sameQuestions(a: MentionHistoryPoint, b: MentionHistoryPoint): boolean {
  return !!a.promptSetHash && a.promptSetHash === b.promptSetHash;
}

function sameEngines(a: MentionHistoryPoint, b: MentionHistoryPoint): boolean {
  const key = (p: MentionHistoryPoint) => [...(p.enginesChecked ?? [])].sort().join(",");
  return key(a) === key(b);
}

/** Why two scores can't be compared — null when they can. */
export type ComparisonBreak = "questions" | "engines" | null;

/**
 * The backend's rule (store.compare_runs, SGL-88): comparable only with the
 * same questions AND the same engines. The score is a mean over whichever
 * engines answered, so one provider's outage would otherwise read as a
 * decline the customer never had.
 */
function comparisonBreak(a: MentionHistoryPoint, b: MentionHistoryPoint): ComparisonBreak {
  if (!sameQuestions(a, b)) return "questions";
  if (!sameEngines(a, b)) return "engines";
  return null;
}

/** Points worth drawing, oldest first: measured, and scored with a location. */
function trendPoints(h: MentionHistory | null): MentionHistoryPoint[] {
  return (h?.series ?? [])
    .filter((p) => p.reliable && typeof p.score === "number")
    .slice()
    .reverse();
}

export interface Movement {
  /** Same questions and same engines, so `delta` is a real change. */
  comparable: boolean;
  /** What differed when not comparable; null when comparable. */
  reason: ComparisonBreak;
  delta: number;
  previousDate: string;
  latestDate: string;
}

/**
 * The move between the last two plotted points, or null with fewer than two.
 *
 * The single rule for "did the score change": the trend card and the score
 * headline both read it, so they cannot disagree. They did when the headline
 * printed the run's stored score_delta, which runs recorded before question
 * sets and locations were tracked carry as 0 — "No change since last check"
 * over a trend that correctly said there was nothing to compare.
 */
export function latestMovement(h: MentionHistory | null): Movement | null {
  const pts = trendPoints(h);
  if (pts.length < 2) return null;
  const last = pts[pts.length - 1];
  const prev = pts[pts.length - 2];
  const reason = comparisonBreak(prev, last);
  return {
    comparable: reason === null,
    reason,
    delta: (last.score as number) - (prev.score as number),
    previousDate: prev.runDate,
    latestDate: last.runDate,
  };
}

/**
 * The movement the score headline may state about the run it is showing:
 * only a comparable one, and only when the trend's latest point IS that run
 * (history is per business name, the headline is the latest run overall).
 * Anything else states nothing — never "no change" without a comparison.
 */
export function headlineMovement(h: MentionHistory | null, runAt?: string): Movement | null {
  const move = latestMovement(h);
  if (!move || !move.comparable || !runAt) return null;
  return runAt.slice(0, 10) === move.latestDate ? move : null;
}

/**
 * The score over time (SGL-139 AC2).
 *
 * Three states are kept apart on purpose — the ticket's "no change" vs "only
 * one run so far" distinction:
 *   - no points  -> nothing measured yet
 *   - one point  -> "Only one check so far"
 *   - two or more-> a line, with the latest movement stated in words
 * A segment where the questions or the answering engines changed is drawn
 * dashed and the caption says the two scores are not comparable, instead of
 * presenting a change in what was measured as a change in visibility.
 *
 * The cadence line only claims automatic re-checks once one has actually
 * happened for this customer. Promising them before that is SGL-291 again.
 */
export const TrendCard: React.FC<{ history: MentionHistory | null; hasRun: boolean }> = ({ history, hasRun }) => {
  const pts = trendPoints(history);
  const scheduled = (history?.series ?? []).some((p) => p.trigger === "scheduled");
  const cadence = scheduled
    ? "We re-check automatically every 15 days, or press Run check any time."
    : "Press Run check again after you've published something new to add the next point.";

  if (pts.length < 2) {
    return (
      <div className="avm-card">
        <header>
          <h2>Visibility trend</h2>
          <span className="avm-preview-tag">{pts.length === 1 ? "Only one check so far" : "Not measured yet"}</span>
        </header>
        <NotMeasuredYet loading={false}
          what={pts.length === 1 || hasRun
            ? `Your next check adds the second point and this becomes a trend line. ${cadence}`
            : "Run your first check to start. Once there are two, this becomes a trend line."} />
      </div>
    );
  }

  const W = 320, H = 120, PAD = 14;
  const x = (i: number) => PAD + (i * (W - 2 * PAD)) / (pts.length - 1);
  const y = (v: number) => H - PAD - (v / 100) * (H - 2 * PAD);
  const last = pts[pts.length - 1];
  const { comparable, reason, delta, previousDate } = latestMovement(history) as Movement;
  const fmt = (d: string) => new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" });

  let caption: string;
  if (!comparable) caption = reason === "engines"
    ? "Not every AI engine answered both checks, so these two scores aren't directly comparable."
    : "The questions changed since the previous check, so these two scores aren't directly comparable.";
  else if (delta === 0) caption = `No change since the previous check on ${fmt(previousDate)}.`;
  else caption = `${delta > 0 ? "Up" : "Down"} ${Math.abs(delta)} points since the previous check on ${fmt(previousDate)}.`;

  return (
    <div className="avm-card">
      <header>
        <h2>Visibility trend</h2>
        <span className="avm-live-tag">Live</span>
      </header>
      <div className="avm-chart">
        <svg viewBox={`0 0 ${W} ${H}`} role="img"
          aria-label={`AI visibility score over ${pts.length} checks, latest ${last.score}`}>
          <line x1={PAD} x2={W - PAD} y1={y(50)} y2={y(50)} className="avm-trend-grid" />
          {pts.slice(1).map((p, i) => (
            <line key={p.runDate} x1={x(i)} y1={y(pts[i].score as number)}
              x2={x(i + 1)} y2={y(p.score as number)}
              className={comparisonBreak(pts[i], p) === null ? "avm-trend-seg" : "avm-trend-seg avm-trend-break"} />
          ))}
          {pts.map((p, i) => (
            <circle key={p.runDate} cx={x(i)} cy={y(p.score as number)} r={4} className="avm-trend-pt">
              <title>{`${fmt(p.runDate)}: ${p.score}${p.trigger === "scheduled" ? " (automatic)" : ""}`}</title>
            </circle>
          ))}
        </svg>
      </div>
      <p className="avm-trend-caption">{caption}</p>
      {history?.truncated && (
        <p className="avm-trend-note">Some earlier checks were recorded under a different business name and aren't shown.</p>
      )}
      <p className="avm-trend-note">{cadence}</p>
    </div>
  );
};

/** Honest empty state — used where we have no measurement, never fake data. */
const NotMeasuredYet: React.FC<{ loading: boolean; what: string }> = ({ loading, what }) => (
  <div className="avm-empty">
    <p>{loading ? "Checking the AI engines…" : "We haven't run this check yet."}</p>
    <p className="avm-empty-sub">{what}</p>
  </div>
);

/* ─────────────────────────── styles (ported from the mockup) ─────────── */

const STYLES = `
.avm-scope {
  /* Neutrals are intentionally plain gray, not sage-tinted — the mockup's
     pale-green wash (--bg, --surface-2, --line, --faint) was an artifact of
     being designed in isolation, not a real brand choice, and read as "the
     whole window is green" once embedded in the real app. Primary stays
     close to Sagelyn's actual brand green (#2f4941); the gold accent is a
     deliberate, approved choice (ring / checkmarks / trend line / chips). */
  --avm-surface: #ffffff; --avm-surface-2: #f7f7f6;
  --avm-ink: #1a201d; --avm-muted: #666f6b; --avm-faint: #9aa19c; --avm-line: #e5e7e4;
  --avm-primary: #2f4941; --avm-primary-soft: #eef1ef; --avm-accent: #c6873a;
  --avm-good: #2f9e6a; --avm-good-bg: #e4f3ea;
  --avm-warn: #d8a24f; --avm-warn-bg: #faf0dc;
  --avm-bad: #c05a48; --avm-bad-bg: #f7e5e1;
  --avm-shadow: 0 1px 2px rgba(18,32,27,.04), 0 8px 24px -12px rgba(18,32,27,.14);
  --avm-radius: 14px;
  color: var(--avm-ink);
  font-family: "Hanken Grotesk", system-ui, sans-serif; font-size: 15px; line-height: 1.5;
  -webkit-font-smoothing: antialiased;
}
/* Dark tokens follow the app's real theme convention (see index.css:34-42),
   not the bare OS media query — otherwise this card goes dark whenever the
   OS prefers dark even when the app itself is explicitly set to Light, and
   never goes dark when the app is explicitly set to Dark. */
html.dark .avm-scope {
  --avm-surface: #1a211d; --avm-surface-2: #202722;
  --avm-ink: #eceeec; --avm-muted: #a3aaa6; --avm-faint: #7a827e; --avm-line: #313830;
  --avm-primary: #7fb5a3; --avm-primary-soft: #223028; --avm-accent: #e0a95c;
  --avm-good: #4fbb84; --avm-good-bg: #16352a;
  --avm-warn: #e0b060; --avm-warn-bg: #352c1a;
  --avm-bad: #dd7a68; --avm-bad-bg: #37211d;
  --avm-shadow: 0 1px 2px rgba(0,0,0,.3), 0 10px 30px -14px rgba(0,0,0,.5);
}
@media (prefers-color-scheme: dark) {
  html[data-theme-preference="system"] .avm-scope {
    --avm-surface: #1a211d; --avm-surface-2: #202722;
    --avm-ink: #eceeec; --avm-muted: #a3aaa6; --avm-faint: #7a827e; --avm-line: #313830;
    --avm-primary: #7fb5a3; --avm-primary-soft: #223028; --avm-accent: #e0a95c;
    --avm-good: #4fbb84; --avm-good-bg: #16352a;
    --avm-warn: #e0b060; --avm-warn-bg: #352c1a;
    --avm-bad: #dd7a68; --avm-bad-bg: #37211d;
    --avm-shadow: 0 1px 2px rgba(0,0,0,.3), 0 10px 30px -14px rgba(0,0,0,.5);
  }
}
.avm-scope * { box-sizing: border-box; }
.avm-scope h1, .avm-scope h2, .avm-scope h3 { font-family: "Fraunces", Georgia, serif; margin: 0; font-weight: 600; letter-spacing: -.01em; text-wrap: balance; }
.avm-num { font-variant-numeric: tabular-nums; }
.avm-eyebrow { font-size: 11px; font-weight: 600; letter-spacing: .09em; text-transform: uppercase; color: var(--avm-muted); }

.avm-card { background: var(--avm-surface); border: 1px solid var(--avm-line); border-radius: var(--avm-radius); box-shadow: var(--avm-shadow); padding: 20px; }
.avm-card > header { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; margin-bottom: 16px; }
.avm-card > header h2 { font-size: 17px; }
.avm-card > header .avm-meta { font-size: 12px; color: var(--avm-muted); font-weight: 600; white-space: nowrap; }

.avm-grid { display: grid; gap: 16px; }
.avm-hero { grid-template-columns: minmax(280px, 1.05fr) 1.4fr; }
.avm-cols-2 { grid-template-columns: 1.3fr 1fr; }
@media (max-width: 860px) { .avm-hero, .avm-cols-2 { grid-template-columns: 1fr; } }

.avm-score-card { display: flex; gap: 22px; align-items: center; flex-wrap: wrap; }
.avm-ring { position: relative; flex: none; width: 132px; height: 132px; }
.avm-ring svg { transform: rotate(-90deg); }
.avm-ring .avm-val { position: absolute; inset: 0; display: grid; place-content: center; text-align: center; }
.avm-ring .avm-val b { font-family: "Fraunces", serif; font-size: 40px; font-weight: 600; line-height: 1; }
.avm-ring .avm-val span { font-size: 11px; color: var(--avm-muted); font-weight: 600; letter-spacing: .04em; text-transform: uppercase; }
.avm-score-meta h2 { font-size: 19px; margin-bottom: 4px; }
.avm-delta { display: inline-flex; align-items: center; gap: 5px; font-weight: 700; font-size: 13px; padding: 3px 9px; border-radius: 999px; background: var(--avm-good-bg); color: var(--avm-good); }
.avm-score-meta p { color: var(--avm-muted); font-size: 13px; margin: 10px 0 0; max-width: 30ch; }

.avm-kpis { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
@media (min-width: 560px) { .avm-kpis { grid-template-columns: repeat(4, 1fr); } }
.avm-kpi { background: var(--avm-surface); border: 1px solid var(--avm-line); border-radius: var(--avm-radius); box-shadow: var(--avm-shadow); padding: 16px 18px; }
.avm-kpi b { font-family: "Fraunces", serif; font-size: 27px; font-weight: 600; display: block; line-height: 1; }
.avm-kpi .avm-lbl { font-size: 12px; color: var(--avm-muted); font-weight: 600; margin-top: 7px; }
.avm-kpi .avm-lbl .avm-up { color: var(--avm-good); }

.avm-matrix { overflow-x: auto; }
.avm-mx { border-collapse: collapse; width: 100%; min-width: 480px; }
.avm-mx th, .avm-mx td { padding: 9px 8px; text-align: center; }
.avm-mx thead th { font-size: 11px; font-weight: 700; color: var(--avm-muted); letter-spacing: .03em; border-bottom: 1px solid var(--avm-line); }
.avm-mx thead th.avm-q { text-align: left; }
.avm-mx tbody td.avm-q { text-align: left; font-size: 13px; font-weight: 600; color: var(--avm-ink); white-space: nowrap; }
.avm-mx tbody tr + tr td { border-top: 1px solid var(--avm-line); }
.avm-cell { display: inline-grid; place-items: center; width: 26px; height: 26px; border-radius: 8px; font-size: 13px; font-weight: 700; }
.avm-cell.ok { background: var(--avm-good-bg); color: var(--avm-good); }
.avm-cell.mid { background: var(--avm-warn-bg); color: var(--avm-warn); }
.avm-cell.no { background: var(--avm-bad-bg); color: var(--avm-bad); }
.avm-cell.unknown { background: var(--avm-muted-bg, rgba(127,127,127,.12)); color: var(--avm-muted, #8a8a94); font-weight: 600; }
.avm-nudge { margin: 0 0 12px; }
.avm-qtag { display: inline-block; margin-left: 8px; padding: 1px 7px; border-radius: 999px; font-size: 10.5px; font-weight: 700; letter-spacing: .02em; vertical-align: 1px; border: 1px solid var(--avm-line); color: var(--avm-muted); }
.avm-qtag-generated { color: var(--avm-warn); border-color: var(--avm-warn); }
.avm-src { margin: 0 0 10px; font-size: 12.5px; line-height: 1.5; }
.avm-src-real { color: var(--avm-good); }
.avm-src-sample { color: var(--avm-warn); }
.avm-legend { display: flex; gap: 14px; flex-wrap: wrap; margin-top: 14px; font-size: 12px; color: var(--avm-muted); font-weight: 600; }
.avm-legend i { display: inline-block; width: 11px; height: 11px; border-radius: 3px; margin-right: 5px; vertical-align: -1px; }

.avm-bars { display: flex; flex-direction: column; gap: 13px; }
.avm-bar-row .avm-bar-top { display: flex; justify-content: space-between; font-size: 13px; font-weight: 600; margin-bottom: 5px; }
.avm-bar-row .avm-rate { color: var(--avm-muted); }
.avm-bar-row.avm-you .avm-bar-top { color: var(--avm-primary); }
.avm-track { height: 9px; background: var(--avm-surface-2); border-radius: 999px; overflow: hidden; }
.avm-fill { height: 100%; border-radius: 999px; background: var(--avm-faint); }
.avm-bar-row.avm-you .avm-fill { background: var(--avm-primary); }
.avm-tag-you { font-size: 10px; font-weight: 700; letter-spacing: .04em; background: var(--avm-primary); color: var(--avm-surface); padding: 1px 6px; border-radius: 5px; margin-left: 6px; }

.avm-src { display: flex; flex-direction: column; gap: 2px; }
.avm-src-row { display: flex; align-items: center; gap: 11px; padding: 9px 4px; }
.avm-src-row + .avm-src-row { border-top: 1px solid var(--avm-line); }
.avm-src-row .avm-dot { width: 9px; height: 9px; border-radius: 50%; flex: none; }
.avm-src-row .avm-name { font-weight: 600; font-size: 14px; flex: 1; }
.avm-src-row .avm-st { font-size: 12px; font-weight: 700; }
.avm-st.ok { color: var(--avm-good); } .avm-st.mid { color: var(--avm-warn); } .avm-st.no { color: var(--avm-faint); }
.avm-dot.ok { background: var(--avm-good); } .avm-dot.mid { background: var(--avm-warn); } .avm-dot.no { background: var(--avm-faint); }

.avm-chart svg { width: 100%; height: auto; display: block; }
.avm-trend-grid { stroke: var(--avm-line); stroke-width: 1; stroke-dasharray: 2 4; }
.avm-trend-seg { stroke: var(--avm-primary); stroke-width: 2.5; stroke-linecap: round; }
.avm-trend-break { stroke: var(--avm-faint); stroke-dasharray: 4 5; }
.avm-trend-pt { fill: var(--avm-surface); stroke: var(--avm-primary); stroke-width: 2.5; }
.avm-trend-caption { margin: 12px 0 0; font-size: 14px; font-weight: 600; color: var(--avm-ink); }
.avm-trend-note { margin: 6px 0 0; font-size: 12.5px; color: var(--avm-muted); }
.avm-delta.down { background: var(--avm-bad-bg); color: var(--avm-bad); }
.avm-delta.flat { background: var(--avm-surface-2); color: var(--avm-muted); }

.avm-feed-card { border-color: color-mix(in srgb, var(--avm-primary) 30%, var(--avm-line)); }
.avm-feed { display: flex; flex-direction: column; }
.avm-act { display: grid; grid-template-columns: auto 1fr; gap: 14px; padding: 14px 0; }
.avm-act + .avm-act { border-top: 1px solid var(--avm-line); }
.avm-act .avm-when { font-size: 11px; font-weight: 700; color: var(--avm-muted); letter-spacing: .02em; white-space: nowrap; padding-top: 2px; }
.avm-act .avm-what { font-size: 14px; font-weight: 600; }
.avm-act .avm-impact { display: flex; align-items: center; gap: 5px; margin-top: 5px; font-size: 12.5px; font-weight: 600; color: var(--avm-good); }
.avm-act .avm-impact::before { content: "\\2192"; color: var(--avm-good); }
.avm-headline-badge { display: inline-flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; color: var(--avm-good); white-space: nowrap; }
.avm-feed-note { margin: -6px 0 4px; font-size: 13px; color: var(--avm-muted); max-width: 64ch; }
.avm-chip { display: inline-block; font-size: 10px; font-weight: 700; letter-spacing: .03em; padding: 2px 7px; border-radius: 6px; margin-left: 8px; vertical-align: 1px; white-space: nowrap; }
.avm-chip.appr { background: var(--avm-good-bg); color: var(--avm-good); }
.avm-chip.auto { background: var(--avm-surface-2); color: var(--avm-muted); }

.avm-preview-tag { display: inline-flex; align-items: center; gap: 5px; font-size: 10px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; color: var(--avm-warn); background: var(--avm-warn-bg); padding: 3px 8px; border-radius: 999px; }
.avm-topbar { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 12px; flex-wrap: wrap; }
.avm-topbar-right { display: flex; align-items: center; gap: 12px; }
.avm-lastrun { font-size: 12px; color: var(--avm-muted); }
.avm-run { font-family: inherit; font-size: 13px; font-weight: 700; padding: 8px 16px; border-radius: 9px; border: 1px solid var(--avm-primary); background: var(--avm-primary); color: #fff; cursor: pointer; white-space: nowrap; }
.avm-run:disabled { opacity: .6; cursor: default; }
.avm-run-alt { background: transparent; color: var(--avm-primary); text-decoration: none; display: inline-block; }
.avm-notice { background: var(--avm-surface-2); border: 1px solid var(--avm-line); border-radius: 10px; padding: 12px 16px; margin-bottom: 14px; font-size: 14px; color: var(--avm-muted); }
.avm-notice-action { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; border-left: 3px solid var(--avm-warn); }
.avm-notice-action b { color: var(--avm-ink); display: block; margin-bottom: 2px; }
.avm-notice-action p { margin: 0; font-size: 13px; }
.avm-src-count { font-size: 12px; color: var(--avm-muted); font-variant-numeric: tabular-nums; white-space: nowrap; }
.avm-empty { padding: 22px 4px; color: var(--avm-muted); }
.avm-empty p { margin: 0; font-size: 14px; }
.avm-empty .avm-empty-sub { margin-top: 6px; font-size: 13px; color: var(--avm-faint); max-width: 46ch; }
.avm-live-tag { display: inline-flex; align-items: center; gap: 5px; font-size: 10px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; color: var(--avm-good); background: var(--avm-good-bg); padding: 3px 8px; border-radius: 999px; }
.avm-section-label { margin: 26px 0 14px; display: flex; align-items: center; gap: 10px; }
.avm-footer-note { margin-top: 26px; font-size: 12.5px; color: var(--avm-muted); text-align: center; line-height: 1.6; }

/* Google | Bing side by side (SGL-134). One column under 860px, like .avm-cols-2. */
.avm-engines { grid-template-columns: 1fr 1fr; align-items: start; }
@media (max-width: 860px) { .avm-engines { grid-template-columns: 1fr; } }
.avm-engine { display: flex; flex-direction: column; gap: 16px; min-width: 0; }
.avm-engine .avm-kpis { grid-template-columns: repeat(2, 1fr); }
.avm-engine .avm-notice { margin-bottom: 0; }
.avm-engine-head { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; }
.avm-engine-head h2 { font-size: 16px; }
.avm-engine-head .avm-meta { font-size: 12px; color: var(--avm-muted); font-weight: 600; }
.avm-engine-error { border-color: var(--avm-bad); border-left: 3px solid var(--avm-bad); background: var(--avm-bad-bg); }
.avm-engine-error b { color: var(--avm-bad); display: block; margin-bottom: 2px; }
.avm-engine-error p { margin: 0; font-size: 13px; color: var(--avm-ink); }
.avm-engine-error .avm-engine-code { margin-top: 6px; font-size: 12px; color: var(--avm-muted); }
.avm-bad-tag { display: inline-flex; align-items: center; gap: 5px; font-size: 10px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; color: var(--avm-bad); background: var(--avm-bad-bg); padding: 3px 8px; border-radius: 999px; }
.avm-diff-list { list-style: none; margin: 0; padding: 0; }
.avm-diff-text { font-size: 13.5px; min-width: 0; overflow-wrap: anywhere; }
.avm-subhead { margin: 18px 0 6px; font-family: inherit !important; }
.avm-errs { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 8px 16px; margin: 0; }
.avm-errs div { display: flex; justify-content: space-between; gap: 8px; padding: 6px 0; border-bottom: 1px solid var(--avm-line); font-size: 13px; }
.avm-errs dt { color: var(--avm-muted); }
.avm-errs dd { margin: 0; font-weight: 700; }
.avm-missing { display: flex; gap: 10px; align-items: flex-start; padding: 14px 16px; border: 1px dashed var(--avm-line); border-radius: 10px; background: var(--avm-surface-2); }
.avm-missing b { display: block; font-size: 13px; color: var(--avm-ink); margin-bottom: 2px; }
.avm-missing p { margin: 0; font-size: 12.5px; color: var(--avm-muted); }
.avm-rotate { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; font-size: 13px; color: var(--avm-ink); background: var(--avm-warn-bg); border-radius: 10px; padding: 10px 14px; }
.avm-rotate a { font-weight: 700; color: var(--avm-ink); text-underline-offset: 3px; }
`;

/* ─────────────────────────── small building blocks ────────────────────── */

const ScoreRing: React.FC<{ score: number }> = ({ score }) => {
  const c = 2 * Math.PI * 58;
  const offset = c * (1 - score / 100);
  return (
    <div className="avm-ring">
      <svg width="132" height="132" viewBox="0 0 132 132" aria-hidden="true">
        <circle cx="66" cy="66" r="58" fill="none" stroke="var(--avm-line)" strokeWidth="12" />
        <circle cx="66" cy="66" r="58" fill="none" stroke="var(--avm-accent)" strokeWidth="12"
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={offset} />
      </svg>
      <div className="avm-val">
        <b className="avm-num">{score}</b>
        <span>/ 100</span>
      </div>
    </div>
  );
};

const MatrixLegend: React.FC = () => (
  <div className="avm-legend">
    <span><i style={{ background: "var(--avm-good)" }} />Recommended</span>
    <span><i style={{ background: "var(--avm-warn)" }} />Mentioned, not top</span>
    <span><i style={{ background: "var(--avm-faint)" }} />Absent</span>
  </div>
);

/* ─────────────────────────── real-data cards (GSC, live) ──────────────── */

const fmtPct = (x: number) => `${(x * 100).toFixed(1)}%`;
const fmtNum = (x: number) => x.toLocaleString();

/** Position chip. Bing reports no position for some rows (sent as 0), and that must
 *  read as unknown, never as a green "0.0". */
const PositionCell: React.FC<{ position: number }> = ({ position }) =>
  position > 0 ? (
    <span className={`avm-cell ${position <= 10 ? "ok" : position <= 20 ? "mid" : "no"}`} style={{ width: "auto", padding: "0 8px" }}>{position.toFixed(1)}</span>
  ) : (
    <span className="avm-cell unknown" style={{ width: "auto", padding: "0 8px" }} title="No position reported">—</span>
  );

const LiveRowTable: React.FC<{
  title: string;
  rows: VisibilityRow[];
  keyLabel: string;
  source?: string;
  /** Shown instead of an empty table; omitted, an empty list renders as before. */
  emptyText?: string;
}> = ({ title, rows, keyLabel, source = "Google Search Console", emptyText }) => (
  <div className="avm-card">
    <header><h2>{title}</h2><span className="avm-meta">{source}</span></header>
    {emptyText && rows.length === 0 ? (
      <p className="avm-empty-sub" style={{ margin: 0 }}>{emptyText}</p>
    ) : (
      <div className="avm-matrix">
        <table className="avm-mx" style={{ minWidth: 440 }}>
          <thead>
            <tr>
              <th className="avm-q">{keyLabel}</th>
              <th>Clicks</th><th>Impr.</th><th>CTR</th><th>Pos.</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 6).map((r) => (
              <tr key={r.key}>
                <td className="avm-q" style={{ maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis" }} title={r.key}>{r.key}</td>
                <td className="avm-num">{fmtNum(r.clicks)}</td>
                <td className="avm-num">{fmtNum(r.impressions)}</td>
                <td className="avm-num" style={{ color: "var(--avm-muted)" }}>{fmtPct(r.ctr)}</td>
                <td><PositionCell position={r.position} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

/** The four search KPIs for one engine. `engine` only changes the clicks label. */
const SearchKpis: React.FC<{ summary: VisibilitySummary; engine: string }> = ({ summary, engine }) => (
  <div className="avm-kpis">
    <div className="avm-kpi"><b className="avm-num">{fmtNum(summary.clicks)}</b><div className="avm-lbl">Clicks from {engine}</div></div>
    <div className="avm-kpi"><b className="avm-num">{fmtNum(summary.impressions)}</b><div className="avm-lbl">Search impressions</div></div>
    <div className="avm-kpi"><b className="avm-num">{fmtPct(summary.ctr)}</b><div className="avm-lbl">Click-through rate</div></div>
    {/* 0 is what the backend reports when no row carried a position. */}
    <div className="avm-kpi"><b className="avm-num">{summary.avg_position > 0 ? summary.avg_position.toFixed(1) : "—"}</b><div className="avm-lbl">Avg position</div></div>
  </div>
);

/* ─────────────────────────── Bing Webmaster Tools (SGL-134) ───────────── */


/** Why an owner should care about Bing at all — said where the Bing data is. */
const BING_WHY = "ChatGPT's live browsing uses Bing's index, so a page Bing can't crawl can't be cited.";

/** Straight to the Bing card, where the owner pastes or updates their API key. */
export const BING_CONNECT_PATH = `${CONNECTIONS_PATH}&section=search`;

/** What a failed dataset of a partial pull covers, in the owner's words. */
const BING_DATASET_LABEL: Record<BingDataset, string> = {
  query: "search figures and top queries",
  page: "top pages",
  crawl: "index count and crawl errors",
  crawl_issues: "crawl issues",
};

/** One dataset Bing didn't send this time. Never drawn as zeros or "none". */
const BingMissing: React.FC<{ title: string }> = ({ title }) => (
  <div className="avm-missing" role="status">
    <span className="material-icons" style={{ fontSize: 18, color: "var(--avm-muted)" }} aria-hidden>cloud_off</span>
    <div>
      <b>{title}: couldn't load from Bing</b>
      <p>Bing didn't send this part this time. It's unknown, not zero. Try Refresh in a few minutes.</p>
    </div>
  </div>
);

/**
 * Plain language for chat-service's Bing error codes. Anything else is a reason
 * from the Bing pull itself; it gets a generic line, with the code kept visible
 * underneath so support can act on it.
 */
const BING_ERROR_TEXT: Record<string, string> = {
  not_configured: "Bing reporting isn't set up on our side yet.",
  bing_service_unreachable: "Our Bing data service didn't respond.",
  connection_lookup_failed: "We couldn't check your Bing connection.",
  malformed_response: "Bing's data came back in a form we couldn't read.",
  internal_error: "Something went wrong on our side while reading Bing.",
};

export function bingErrorText(code?: string): string {
  if (code && BING_ERROR_TEXT[code]) return BING_ERROR_TEXT[code];
  const http = code?.match(/^http_(\d+)$/);
  if (http) return `Our Bing data service returned an error (HTTP ${http[1]}).`;
  return "We couldn't pull your data from Bing.";
}

const fmtDate = (d: string) =>
  new Date(`${d.slice(0, 10)}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" });

const EngineHead: React.FC<{ id: string; name: string; children?: React.ReactNode }> = ({ id, name, children }) => (
  <header className="avm-engine-head">
    <h2 id={id}>{name}</h2>
    {children}
  </header>
);

/**
 * The three unmeasured states of the Bing column. Each looks different on purpose
 * (criterion 6): "not connected", "connection expired" and
 * "we couldn't read it" each say so in words, and none borrows the look of a
 * measured empty result.
 */
type EngineNoticeProps = {
  kind: "not_connected" | "needs_reauth" | "key_invalid" | "bing_unavailable" | "error";
  title: string;
  body: React.ReactNode;
  /** Connect / Reconnect label; absent for an error. */
  cta?: string;
  /** Where the CTA goes; Connections by default. */
  href?: string;
  /** Machine reason, shown under an error for support. */
  code?: string;
};

const EngineNotice: React.FC<EngineNoticeProps> = ({ kind, title, body, cta, href = CONNECTIONS_PATH, code }) =>
  kind === "error" ? (
    <div className="avm-notice avm-engine-error" role="alert">
      <b>{title}</b>
      <p>{body}</p>
      {code && <p className="avm-engine-code">Reason: <code>{code}</code></p>}
    </div>
  ) : (
    <div className="avm-notice avm-notice-action" role={kind === "bing_unavailable" ? "status" : undefined}>
      <div>
        <b>{title}</b>
        <p>{body}</p>
      </div>
      {cta && <a className="avm-run" href={href}>{cta}</a>}
    </div>
  );

/** The header tag for an unmeasured engine, so the state reads at a glance. */
const engineStateTag = (status?: string) =>
  status === "not_connected" ? <span className="avm-preview-tag">Not connected</span>
    : status === "needs_reauth" ? <span className="avm-preview-tag">Reconnect needed</span>
      : status === "key_invalid" ? <span className="avm-preview-tag">New key needed</span>
        : status === "bing_unavailable" ? <span className="avm-preview-tag">Bing not responding</span>
          : <span className="avm-bad-tag">Couldn't load</span>;

/** Measured, fully or in part: the data branch of the Bing column. */
export const bingMeasured = (
  bing?: BingSearchPerformance
): bing is Extract<BingSearchPerformance, { status: "ok" | "partial" }> =>
  bing?.status === "ok" || bing?.status === "partial";

/** The Bing column: the shared notices for the unmeasured states, data otherwise. */
const BingColumn: React.FC<{ bing: BingSearchPerformance }> = ({ bing }) => {
  const head = (tag: React.ReactNode) => (
    <EngineHead id="avm-bing-title" name="Bing Webmaster Tools">{tag}</EngineHead>
  );

  // The reminder rides on every connected answer, measured or not.
  const rotation = bing.rotation_due ? (
    <div className="avm-rotate">
      <span>Time to refresh your Bing API key. Your current one still works.</span>
      <a href={BING_CONNECT_PATH}>Update key</a>
    </div>
  ) : null;

  if (!bingMeasured(bing)) {
    const forSite = bing.site ? ` for ${bing.site}` : "";
    const notice = bing.status === "not_connected" ? (
      <EngineNotice kind="not_connected" title="Connect Bing Webmaster Tools"
        body={`See your Bing clicks, queries and index coverage beside Google's. ${BING_WHY}`}
        cta="Connect Bing" href={BING_CONNECT_PATH} />
    ) : bing.status === "needs_reauth" ? (
      <EngineNotice kind="needs_reauth"
        title="Bing Webmaster Tools needs reconnecting"
        body={`Bing now connects with your own API key. Connect again with your API key${forSite} to see your Bing data here.`}
        cta="Reconnect Bing" href={BING_CONNECT_PATH} />
    ) : bing.status === "key_invalid" ? (
      <EngineNotice kind="key_invalid"
        title="Your Bing API key no longer works"
        body={`Bing stopped accepting the key${forSite}, usually because it was deleted or a new one was generated. Generate a new one in Bing Webmaster Tools and paste it in Connections.`}
        cta="Paste a new key" href={BING_CONNECT_PATH} />
    ) : bing.status === "bing_unavailable" ? (
      <EngineNotice kind="bing_unavailable"
        title="Bing isn't responding right now"
        body="Your connection is fine. Bing didn't answer when we asked for your data, so nothing from Bing is shown. Try Refresh in a few minutes." />
    ) : (
      // "error", and anything unrecognised: never let an unknown status fall
      // through to the data branch or to "not connected".
      <EngineNotice kind="error" title="We couldn't read your Bing data"
        body={`${bingErrorText(bing.error)} This is a failure on our side, not a sign you're missing from Bing. Try Refresh in a few minutes.`}
        code={bing.error} />
    );
    return (
      <section className="avm-engine" aria-labelledby="avm-bing-title">
        {head(engineStateTag(bing.status))}
        {notice}
        {rotation}
      </section>
    );
  }

  const failed = bing.status === "partial" ? bing.failed_datasets : [];
  return (
    <section className="avm-engine" aria-labelledby="avm-bing-title">
      {head(
        <>
          <span className="avm-live-tag">Live</span>
          {failed.length > 0 && <span className="avm-preview-tag">Partly loaded</span>}
          {bing.data_through && <span className="avm-meta">Data through {fmtDate(bing.data_through)}</span>}
        </>
      )}
      {failed.length > 0 && (
        <p className="avm-trend-note" style={{ margin: 0 }}>
          Bing didn't send your {failed.map((d) => BING_DATASET_LABEL[d] ?? d).join(", ")} this time.
          Everything else below is current.
        </p>
      )}
      {rotation}
      {bing.summary && bing.queries ? (
        <>
          <SearchKpis summary={bing.summary} engine="Bing" />
          <LiveRowTable title="Top queries" rows={bing.queries} keyLabel="Query" source="Bing Webmaster Tools"
            emptyText="Bing reported no search queries for your site in this period." />
        </>
      ) : (
        <BingMissing title="Search figures and top queries" />
      )}
      {bing.pages ? (
        <LiveRowTable title="Top pages" rows={bing.pages} keyLabel="Page" source="Bing Webmaster Tools"
          emptyText="Bing reported no pages with impressions in this period." />
      ) : (
        <BingMissing title="Top pages" />
      )}
    </section>
  );
};

const fmtPos = (r: VisibilityRow | null) => (r && r.position > 0 ? r.position.toFixed(1) : "unknown");

/**
 * One disagreement as a sentence. Page types only ever claim impressions within
 * the period — never "not indexed", which neither top list can tell us.
 */
export function describeDisagreement(d: SearchDisagreement): { tag: string; text: string } {
  switch (d.type) {
    case "bing_only_query":
      return { tag: "Bing only", text: `Bing shows you for “${d.key}”; Google doesn't, in your top queries.` };
    case "google_only_query":
      return { tag: "Google only", text: `Google shows you for “${d.key}”; Bing doesn't, in your top queries.` };
    case "position_gap":
      return { tag: "Position gap", text: `“${d.key}” is at position ${fmtPos(d.google)} on Google and ${fmtPos(d.bing)} on Bing.` };
    case "google_only_page":
      return { tag: "Google only", text: `${d.key} is one of your top pages on Google, with no Bing impressions in this period.` };
    case "bing_only_page":
      return { tag: "Bing only", text: `${d.key} is one of your top pages on Bing, with no Google impressions in this period.` };
  }
}

/**
 * `compared` is false when neither dimension had rows on both sides: the backend
 * then returns [] without comparing anything, and that must not read as agreement.
 */
const DisagreementsCard: React.FC<{ items: SearchDisagreement[]; compared: boolean }> = ({ items, compared }) => (
  <section className="avm-card" aria-labelledby="avm-disagree-title">
    <header><h2 id="avm-disagree-title">Where Google and Bing disagree</h2></header>
    {items.length === 0 ? (
      <p className="avm-empty-sub" style={{ margin: 0 }}>
        {compared
          ? "No differences worth flagging among your top queries and pages in this period."
          : "Not enough data from both engines to compare yet."}
      </p>
    ) : (
      <ul className="avm-diff-list">
        {items.map((d) => {
          const { tag, text } = describeDisagreement(d);
          return (
            <li className="avm-src-row" key={`${d.type}:${d.key}`}>
              <span className="avm-chip auto" style={{ marginLeft: 0 }}>{tag}</span>
              <span className="avm-diff-text">{text}</span>
            </li>
          );
        })}
      </ul>
    )}
  </section>
);

const CRAWL_ERROR_LABELS: [keyof BingCrawlErrors, string][] = [
  ["code_4xx", "4xx errors"],
  ["code_5xx", "5xx errors"],
  ["blocked_by_robots", "Blocked by robots.txt"],
  ["dns_failures", "DNS failures"],
  ["connection_timeout", "Connection timeouts"],
];

/** `issues` is shown as Bing sent it: its encoding is UNVERIFIED, so nothing is decoded. */
function formatIssues(v: unknown): string {
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) return v.map(String).join(", ");
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

const InIndexTrend: React.FC<{ trend: BingIndexHealth["in_index_trend"] }> = ({ trend }) => {
  if (!trend || trend.length < 2) return null;
  const W = 320, H = 70, PAD = 8;
  const vals = trend.map((p) => p.in_index);
  const lo = Math.min(...vals), hi = Math.max(...vals);
  const x = (i: number) => PAD + (i * (W - 2 * PAD)) / (trend.length - 1);
  const y = (v: number) => (hi === lo ? H / 2 : H - PAD - ((v - lo) / (hi - lo)) * (H - 2 * PAD));
  const first = trend[0], last = trend[trend.length - 1];
  const caption = `${fmtNum(first.in_index)} on ${fmtDate(first.date)} → ${fmtNum(last.in_index)} on ${fmtDate(last.date)}`;
  return (
    <div className="avm-chart" style={{ marginTop: 10 }}>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Pages in Bing's index: ${caption}`}>
        <polyline fill="none" className="avm-trend-seg" points={trend.map((p, i) => `${x(i)},${y(p.in_index)}`).join(" ")} />
      </svg>
      <p className="avm-trend-note">{caption}</p>
    </div>
  );
};

const BingIndexCard: React.FC<{ index: BingIndexHealth }> = ({ index }) => (
  <section className="avm-card" aria-labelledby="avm-bing-index-title">
    <header>
      <h2 id="avm-bing-index-title">Bing index health</h2>
      <span className="avm-meta">Bing Webmaster Tools</span>
    </header>
    <p className="avm-feed-note" style={{ margin: "-6px 0 14px" }}>{BING_WHY}</p>

    {index.crawl_errors === null ? (
      <BingMissing title="Index count and crawl errors" />
    ) : (
    <>
    <div className="avm-kpi" style={{ boxShadow: "none" }}>
      <b className="avm-num">{index.in_index !== null ? fmtNum(index.in_index) : "—"}</b>
      <div className="avm-lbl">
        {index.in_index !== null ? "Pages in Bing's index" : "Pages in Bing's index (not reported)"}
      </div>
    </div>
    <InIndexTrend trend={index.in_index_trend} />

    <h3 className="avm-eyebrow avm-subhead">Crawl errors</h3>
    <p className="avm-trend-note" style={{ margin: "0 0 8px" }}>
      {index.crawl_days
        ? `Added up over the last ${index.crawl_days} day${index.crawl_days === 1 ? "" : "s"} of crawl data from Bing.`
        : "Bing sent no crawl data for this period."}
    </p>
    <dl className="avm-errs">
      {CRAWL_ERROR_LABELS.map(([field, label]) => {
        const v = index.crawl_errors![field];
        return (
          <div key={field}>
            <dt>{label}</dt>
            <dd className="avm-num">{v !== null ? fmtNum(v) : <span title="Bing didn't report this">—</span>}</dd>
          </div>
        );
      })}
    </dl>
    </>
    )}

    <h3 className="avm-eyebrow avm-subhead">Crawl issues</h3>
    {index.crawl_issues === null ? (
      <BingMissing title="Crawl issues" />
    ) : index.crawl_issues.length === 0 ? (
      <p className="avm-empty-sub" style={{ margin: 0 }}>Bing reports no crawl issues for your site.</p>
    ) : (
      <>
        {(index.crawl_issues_total ?? 0) > index.crawl_issues.length && (
          <p className="avm-trend-note" style={{ margin: "0 0 6px" }}>
            Showing {index.crawl_issues.length} of {fmtNum(index.crawl_issues_total ?? 0)} issues.
          </p>
        )}
        <div className="avm-matrix">
          <table className="avm-mx" style={{ minWidth: 400 }}>
            <thead>
              <tr><th className="avm-q">Page</th><th>HTTP</th><th>Issue (as Bing reports it)</th></tr>
            </thead>
            <tbody>
              {index.crawl_issues.map((i) => (
                <tr key={i.url}>
                  <td className="avm-q" style={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis" }} title={i.url}>{i.url}</td>
                  <td className="avm-num">{i.http_code ?? "—"}</td>
                  <td style={{ fontSize: 12.5, color: "var(--avm-muted)" }}>{formatIssues(i.issues)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    )}
  </section>
);

/* ─────────────────────────── the dashboard ─────────────────────────────── */

const AiVisibilityDashboard: React.FC = () => {
  const { idToken } = useAuth();
  const { selectedCustomerId, selectedCustomerName } = useCustomerWorkspace();

  const [data, setData] = useState<SearchPerformance | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [history, setHistory] = useState<MentionHistory | null>(null);

  /** Run history for the trend card. A failure leaves the card on its empty
   *  state rather than blanking the page — the rest is still useful. */
  const loadHistory = useCallback(async () => {
    if (!idToken || !selectedCustomerId) return;
    try {
      setHistory(await fetchMentionHistory(selectedCustomerId, idToken));
    } catch {
      setHistory(null);
    }
  }, [idToken, selectedCustomerId]);

  /** Poll until a run in flight lands. Shared by a freshly started check and
   *  one resumed after the user navigated away and came back. */
  const resumePolling = useCallback(async () => {
    if (!idToken || !selectedCustomerId) return;
    const deadline = Date.now() + 180_000;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 8000));
      const latest = await fetchMentionCheck(selectedCustomerId, idToken);
      if (latest.status !== "running") {
        setMentions(latest);
        setRunNotice(null);
        setRunning(false);
        void loadHistory();
        return;
      }
    }
    setRunNotice("Still running — this is taking longer than usual. Refresh in a minute.");
    setRunning(false);
  }, [idToken, selectedCustomerId, loadHistory]);

  // AI mention check (SGL-135). Fetched independently of Search Console: a
  // customer can have one without the other, and one failing must not blank
  // the other's section.
  const [mentions, setMentions] = useState<MentionCheck | null>(null);
  const [mentionsLoading, setMentionsLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [runNotice, setRunNotice] = useState<string | null>(null);
  const [needsDetail, setNeedsDetail] = useState<{ message: string; reason?: string } | null>(null);

  const load = useCallback(async () => {
    if (!idToken || !selectedCustomerId) return;
    setLoading(true);
    setError(null);
    try {
      const siteOverride = new URLSearchParams(window.location.search).get("site") || undefined;
      const result = await fetchSearchPerformance(selectedCustomerId, idToken, siteOverride);
      setData(result);
    } catch (e: any) {
      setError(e?.message || "Failed to load visibility data");
    } finally {
      setLoading(false);
    }
  }, [idToken, selectedCustomerId]);

  const loadMentions = useCallback(async () => {
    if (!idToken || !selectedCustomerId) return;
    setMentionsLoading(true);
    try {
      const res = await fetchMentionCheck(selectedCustomerId, idToken);
      setMentions(res);
      // A check started before this page was opened (or before the user
      // navigated away) is still running server-side. Pick it back up rather
      // than showing an idle button and inviting a duplicate run.
      if (res.status === "running" && !running) {
        setRunning(true);
        setRunNotice("Still checking the AI engines — this takes about a minute and a half.");
        void resumePolling();
      }
    } catch {
      // Treated as "not measured" rather than surfaced as a page error — the
      // GSC half of this dashboard is still useful on its own.
      setMentions(null);
    } finally {
      setMentionsLoading(false);
    }
  }, [idToken, selectedCustomerId, running, resumePolling]);

  /** Start a check, then poll until it lands (~85s) rather than making the
   *  user refresh and wonder whether anything happened. */
  const runCheck = useCallback(async () => {
    if (!idToken || !selectedCustomerId || running) return;
    setRunning(true);
    setRunNotice(null);
    setNeedsDetail(null);
    try {
      const res = await startMentionCheck(selectedCustomerId, idToken);
      if (res.status === "already_running") {
        // Not a cache hit — a check is genuinely in flight, so join it rather
        // than starting a second one for the same result.
        setRunNotice("A check is already running — picking it up.");
        await resumePolling();
        return;
      }
      setRunNotice("Asking ChatGPT, Perplexity, Gemini and Claude… this takes about a minute and a half.");
      await resumePolling();
      return;
    } catch (e: any) {
      if (e instanceof InsufficientBusinessDetail) setNeedsDetail({ message: e.message, reason: e.reason });
      else setRunNotice(e?.message || "Could not start the check.");
    } finally {
      setRunning(false);
    }
  }, [idToken, selectedCustomerId, running, resumePolling]);

  // Weekly Search Console comparison (SGL-470). Its own fetch: a failure here
  // hides only this card, never the rest of the page.
  const [weekly, setWeekly] = useState<WeeklySearch | null>(null);
  const [weeklyLoading, setWeeklyLoading] = useState(false);
  // Only the latest request may land: switching Brand must never show the
  // previous Brand's week, even when its response arrives last.
  const weeklyRequest = React.useRef(0);
  const loadWeekly = useCallback(async () => {
    if (!idToken || !selectedCustomerId) return;
    const request = ++weeklyRequest.current;
    setWeekly(null);
    setWeeklyLoading(true);
    try {
      const result = await fetchWeeklySearch(selectedCustomerId, idToken);
      if (request === weeklyRequest.current) setWeekly(result);
    } catch {
      if (request === weeklyRequest.current) setWeekly(null);
    } finally {
      if (request === weeklyRequest.current) setWeeklyLoading(false);
    }
  }, [idToken, selectedCustomerId]);

  useEffect(() => {
    load();
    loadMentions();
    loadHistory();
    loadWeekly();
  }, [load, loadMentions, loadHistory, loadWeekly]);

  const liveConnected = data?.connected === true;
  const bing = data?.bing;

  const m = mentions?.status === "ready" ? mentions : null;
  const move = headlineMovement(history, m?.runAt);
  const engineCount = m?.enginesChecked?.length ?? 0;
  // Engines we tried to ask, so a failed or not-set-up engine shows as x/y.
  const enginesAsked = engineCount + (m?.enginesFailed?.length ?? 0) + (m?.enginesUnconfigured?.length ?? 0);
  const mentionRate =
    m && m.totalQuestions ? Math.round(((m.appearedIn ?? 0) / m.totalQuestions) * 100) : null;

  // Same condition the page-2 list always had: only real, connected GSC data.
  const gscOpportunities = liveConnected && data ? data.opportunities ?? [] : [];
  const showProfileFindings = hasProfileFindingsContent(m?.profileFindings);
  // A finished check is on screen, so "not computed for this run" is worth a line.
  const mentionReady = m !== null;

  return (
    <div className="avm-scope">
      <style>{STYLES}</style>

      <div className="avm-topbar">
        <p className="avm-eyebrow" style={{ margin: 0 }}>AI Visibility Manager</p>
        <div className="avm-topbar-right">
          {m?.runAt && (
            <span className="avm-lastrun">
              Last checked {new Date(m.runAt).toLocaleDateString(undefined,
                { month: "short", day: "numeric" })}
            </span>
          )}
          <button className="avm-run" onClick={runCheck} disabled={running || !selectedCustomerId}>
            {running ? "Checking…" : m ? "Run check again" : "Run my first check"}
          </button>
        </div>
      </div>

      {needsDetail && (
        <div className="avm-notice avm-notice-action">
          <div>
            <b>We need a bit more about your business</b>
            <p>{needsDetail.message}</p>
          </div>
          {needsDetail.reason === "location_unknown"
            ? <a className="avm-run avm-run-alt" href={LOCATION_FIX_PATH}>Open business profile</a>
            : <a className="avm-run avm-run-alt" href="/brand-details">Open business settings</a>}
        </div>
      )}
      {runNotice && <div className="avm-notice">{runNotice}</div>}
      {!running && mentions?.lastAttempt && (() => {
        const n = attemptMessage(mentions.lastAttempt);
        return (
          <div className="avm-notice avm-notice-action">
            <div>
              <b>{n.title}</b>
              <p>{n.body}</p>
            </div>
            {n.href && <a className="avm-run avm-run-alt" href={n.href}>{n.cta}</a>}
          </div>
        );
      })()}

      {/* Hero: score + KPIs — REAL (SGL-135) */}
      <div className="avm-grid avm-hero" style={{ marginBottom: 16 }}>
        <div className="avm-card avm-score-card">
          <ScoreRing score={m?.score ?? 0} />
          <div className="avm-score-meta">
            <p className="avm-eyebrow">
              AI Visibility Score{" "}
              {m ? (
                <span className="avm-live-tag" style={{ marginLeft: 6 }}>Live</span>
              ) : (
                <span className="avm-preview-tag" style={{ marginLeft: 6 }}>Not measured yet</span>
              )}
            </p>
            <h2>{scoreHeadline(m, mentionsLoading)}</h2>
            {/* Only when the trend compares this run with a previous one that
                asked the same questions (headlineMovement) — "nothing to
                compare" and "compared, and nothing moved" are different
                claims (SGL-139 AC2). */}
            {move && (
              <span className={`avm-delta${move.delta < 0 ? " down" : move.delta === 0 ? " flat" : ""}`}>
                {move.delta === 0
                  ? "No change since last check"
                  : `${move.delta > 0 ? "▲" : "▼"} ${Math.abs(move.delta)} pts since last check`}
              </span>
            )}
            <p>
              {m
                ? `How often AI recommends you when customers ask — measured across ${
                    (m.enginesChecked ?? []).map(engineLabel).join(", ") || "no engines"
                  }.`
                : "How often AI recommends you when customers ask. We check ChatGPT, Perplexity, Gemini and Claude with the questions your buyers actually type."}
            </p>
          </div>
        </div>

        <div className="avm-kpis">
          <div className="avm-kpi">
            <b className="avm-num">
              {m ? engineCount : "—"}
              {m && <span style={{ fontSize: 16, color: "var(--avm-muted)" }}>/{enginesAsked}</span>}
            </b>
            <div className="avm-lbl">AI engines checked</div>
          </div>
          <div className="avm-kpi">
            <b className="avm-num">{mentionRate !== null ? `${mentionRate}%` : "—"}</b>
            <div className="avm-lbl">Answers you appear in</div>
          </div>
          <div className="avm-kpi">
            <b className="avm-num">{m ? (m.competitors?.length ?? 0) : "—"}</b>
            <div className="avm-lbl">Competitors named instead</div>
          </div>
          <div className="avm-kpi">
            <b className="avm-num">{m ? (m.totalQuestions ?? 0) : "—"}</b>
            {/* totalQuestions is scored_rows — one row per question PER ENGINE.
                Labelling 36 as "buyer questions" when the table below lists 12
                is the kind of thing a sceptical prospect counts and catches. */}
            <div className="avm-lbl">AI answers checked</div>
          </div>
        </div>
      </div>

      {/* Where you appear + competitors */}
      <div className="avm-grid avm-cols-2" style={{ marginBottom: 16 }}>
        <div className="avm-card">
          <header>
            <h2>Where you appear</h2>
            {m ? <span className="avm-live-tag">Live</span>
               : <span className="avm-preview-tag">Not measured yet</span>}
          </header>
          {m?.matrix?.rows?.length ? <PromptSourceNote source={m.promptSource} reason={m.promptSourceReason} /> : null}
          {m?.matrix?.rows?.length ? (
            <>
              <div className="avm-matrix">
                <table className="avm-mx">
                  <thead>
                    <tr>
                      <th className="avm-q">Customer asks AI…</th>
                      {m.matrix.engines.map((e) => <th key={e}>{engineLabel(e)}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {m.matrix.rows.map((row) => (
                      <tr key={row.question}>
                        <td className="avm-q">
                          “{row.question}”
                          {m.promptSource === "generated" && row.source && (
                            <span className={`avm-qtag avm-qtag-${row.source}`}
                                  title={row.source === "generated"
                                    ? "Written from the services on your profile"
                                    : "One of our standard questions for your category"}>
                              {row.source === "generated" ? "Written" : "Standard"}
                            </span>
                          )}
                        </td>
                        {row.cells.map((role, i) => {
                          const c = roleToCell(role);
                          return (
                            <td key={i}>
                              <span className={`avm-cell ${c}`} title={roleTitle(role)}>
                                {CELL_LABEL[c]}
                              </span>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <MatrixLegend />
              <EngineGapsNote m={m} />
            </>
          ) : (
            <NotMeasuredYet loading={mentionsLoading}
              what="We ask each engine the questions your buyers actually type, then look for you in answers that had no reason to mention you." />
          )}
        </div>

        <div className="avm-card">
          <header>
            <h2>Who gets recommended instead</h2>
            {m ? <span className="avm-live-tag">Live</span>
               : <span className="avm-preview-tag">Not measured yet</span>}
          </header>
          {m?.competitors?.length ? (
          <div className="avm-bars">
            {competitorRows(m, selectedCustomerName).map((c) => (
              <div className={`avm-bar-row ${c.you ? "avm-you" : ""}`} key={c.name}>
                <div className="avm-bar-top">
                  <span>{c.name}{c.you && <span className="avm-tag-you">YOU</span>}</span>
                  <span className="avm-rate avm-num">{c.timesNamed}×</span>
                </div>
                <div className="avm-track"><div className="avm-fill" style={{ width: `${c.pct}%` }} /></div>
              </div>
            ))}
          </div>
          ) : (
            <NotMeasuredYet loading={mentionsLoading}
              what="When an engine names other businesses and not you, they show up here — ranked by how often." />
          )}
        </div>
      </div>

      {/* SGL-510: Claude with search vs from memory */}
      {m && <ClaudeCard m={m} />}

      {/* Trend (real run history) + sources */}
      <div className="avm-grid avm-cols-2" style={{ marginBottom: 16 }}>
        <TrendCard history={history} hasRun={!!m} />

        <div className="avm-card">
          <header>
            <h2>Sources AI cites in your category</h2>
            {m?.citedSources?.length ? <span className="avm-live-tag">Live</span>
                                     : <span className="avm-preview-tag">Not measured yet</span>}
          </header>
          {m?.citedSources?.length ? (
            <>
              <div className="avm-src">
                {m.citedSources.slice(0, 8).map((src: CitedSource) => (
                  <div className="avm-src-row" key={src.domain}>
                    <span className={`avm-dot ${src.is_your_site ? "ok" : "mid"}`} />
                    <span className="avm-name">
                      {src.domain}
                      {src.is_your_site && (
                        <span style={{ fontWeight: 500, color: "var(--avm-muted)", fontSize: 12 }}>
                          {" "}· your site
                        </span>
                      )}
                    </span>
                    <span className="avm-src-count">
                      {src.times_cited} answer{src.times_cited === 1 ? "" : "s"}
                    </span>
                  </div>
                ))}
              </div>
              <p className="avm-empty-sub" style={{ marginTop: 12 }}>
                Where the engines looked when answering questions in your category.
                Being present on these is how you get cited.
              </p>
            </>
          ) : (
            <NotMeasuredYet loading={mentionsLoading}
              what="Which sites the engines lean on when answering questions in your category — that's where being present gets you cited." />
          )}
        </div>
      </div>

      {/* Actions feed. The Business Profile changes the owner approved are the
          one thing recorded today (SGL-328 #2); everything else the Visibility
          Manager does is not logged yet, and the placeholder says so. */}
      <div className="avm-card avm-feed-card">
        <header>
          <h2>You &amp; your Visibility Manager</h2>
          {!hasEditOutcomeList(m?.profileEditOutcomes) && <span className="avm-preview-tag">Not measured yet</span>}
        </header>
        <p className="avm-feed-note">
          You call the shots — ask for a blog, a post, a fix, and it drafts and publishes them.
          It spots opportunities; you approve.
        </p>
        {hasEditOutcomesContent(m?.profileEditOutcomes, mentionReady) && (
          <ProfileEditOutcomes value={m?.profileEditOutcomes} checked={mentionReady} />
        )}
        {!hasEditOutcomeList(m?.profileEditOutcomes) && (
          <NotMeasuredYet loading={false}
            what="Once your Visibility Manager starts acting on your behalf, everything it does — and what it changed — is logged here." />
        )}
      </div>

      {/* Opportunities — Business Profile findings (SGL-328 AC4) first, then
          Search Console page-2 queries. One card, shown when either has
          something: a customer without Search Console still has profile
          findings, and one without a Business Profile still has page-2 rows. */}
      {(showProfileFindings || gscOpportunities.length > 0) && (
        <section className="avm-card" style={{ marginTop: 16 }} aria-labelledby="avm-opportunities-title">
          <header>
            <h2 id="avm-opportunities-title">Opportunities</h2>
            {!showProfileFindings && <span className="avm-meta">real impressions, ranking page 2+</span>}
          </header>
          <ProfileFindings value={m?.profileFindings} />
          {gscOpportunities.length > 0 && (
            <>
              {showProfileFindings && (
                <p className="avm-eyebrow" style={{ margin: "22px 0 4px" }}>
                  From Google Search Console · real impressions, ranking page 2+
                </p>
              )}
              <div className="avm-src">
                {gscOpportunities.slice(0, 5).map((o) => (
                  <div className="avm-src-row" key={o.key}>
                    <span className="avm-dot" style={{ background: "var(--avm-accent)" }} />
                    <span className="avm-name" style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={o.key}>{o.key}</span>
                    <span className="avm-num" style={{ fontSize: 12, color: "var(--avm-muted)", marginRight: 8 }}>{fmtNum(o.impressions)} impr.</span>
                    <span className={`avm-cell ${o.position <= 10 ? "ok" : o.position <= 20 ? "mid" : "no"}`} style={{ width: "auto", padding: "0 8px" }}>{o.position.toFixed(1)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>
      )}

      <WeeklySearchCard value={weekly} loading={weeklyLoading} />

      {/* Search performance — LIVE Google Search Console data, with Bing Webmaster
          Tools beside it once the backend sends it (SGL-134). */}
      <div className="avm-section-label">
        <h2 style={{ fontSize: 17 }}>Search performance</h2>
        <span className="avm-live-tag">{bingMeasured(bing) ? "Live · Google & Bing" : "Live · Google Search Console"}</span>
        {(liveConnected || bingMeasured(bing)) && (
          <button
            onClick={load}
            disabled={loading}
            style={{ marginLeft: "auto", fontFamily: "Hanken Grotesk, sans-serif", fontSize: 12, fontWeight: 600, color: "var(--avm-muted)", background: "var(--avm-surface)", border: "1px solid var(--avm-line)", borderRadius: 8, padding: "6px 12px", cursor: "pointer" }}
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        )}
      </div>

      {loading && !data && (
        <div className="avm-card" style={{ textAlign: "center", padding: "40px 20px", color: "var(--avm-muted)" }}>Loading…</div>
      )}

      {error && (
        <div className="avm-card" style={{ borderColor: "var(--avm-bad)", color: "var(--avm-bad)" }}>{error}</div>
      )}

      {/* Only a response that carries `bing` gets the Bing column. A failed fetch, or a
          backend from before SGL-134, shows no Bing state at all rather than one we
          did not measure (criterion 6). */}
      <div className={bing ? "avm-grid avm-engines" : undefined}>
        <section className={bing ? "avm-engine" : undefined} aria-labelledby={bing ? "avm-google-title" : undefined}>
          {bing && (
            <EngineHead id="avm-google-title" name="Google Search Console">
              {liveConnected && <span className="avm-live-tag">Live</span>}
            </EngineHead>
          )}

          {!loading && !error && !liveConnected && (
            <div className="avm-card" style={{ textAlign: "center", padding: "40px 20px" }}>
              <p style={{ color: "var(--avm-muted)", maxWidth: "48ch", margin: "0 auto" }}>
                {data?.reason === "no_website_on_profile"
                  ? "Add your website to your business profile so we can find your Search Console property."
                  : `We couldn't find Search Console data for ${data?.site || "your website"} yet.`}
              </p>
            </div>
          )}

          {liveConnected && data && (
            <>
              <div style={bing ? undefined : { marginBottom: 16 }}>
                <SearchKpis summary={data.summary!} engine="Google" />
              </div>

              <div className={bing ? "avm-grid" : "avm-grid avm-cols-2"}>
                <LiveRowTable title="Top queries" rows={data.queries ?? []} keyLabel="Query" />
                <LiveRowTable title="Top pages" rows={data.pages ?? []} keyLabel="Page" />
              </div>
            </>
          )}
        </section>

        {bing && <BingColumn bing={bing} />}
      </div>

      {bingMeasured(bing) && (
        <div className={liveConnected ? "avm-grid avm-cols-2" : "avm-grid"} style={{ marginTop: 16 }}>
          {/* The backend compares only when both engines were measured. */}
          {liveConnected ? (
            <DisagreementsCard
              items={data?.disagreements ?? []}
              compared={
                (!!data?.queries?.length && !!bing.queries?.length) ||
                (!!data?.pages?.length && !!bing.pages?.length)
              }
            />
          ) : null}
          <BingIndexCard index={bing.index} />
        </div>
      )}

      {/* SGL-510: Brave (Claude's search index), beside Google and Bing above */}
      {m?.braveIndex && (
        <div className="avm-grid" style={{ marginTop: 16 }}>
          <BraveIndexCard index={m.braveIndex} />
        </div>
      )}

      <GbpProfileSection idToken={idToken} customerId={selectedCustomerId} />

      <p className="avm-footer-note">
        Sections marked <b>Preview</b> show sample data — they populate automatically once the AI mention check
        and competitor monitoring ship. <b>Live</b> sections are your real Google Search Console, Bing Webmaster
        Tools and Google Business Profile data today.
      </p>
    </div>
  );
};

export default AiVisibilityDashboard;
